import {
  Avatar,
  Box,
  Button,
  Container,
  CssBaseline,
  Link,
  Paper,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import "./login.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoginRequest } from "../../app/models/requestModels/loginRequest";
import { useAppDispatch } from "../../app/features/store/store";
import { userLogin } from "../../app/features/login/loginSlice";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import Loader from "../../components/common/loader/Loader";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState } from "react";

export function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"© "}
      {new Date().getFullYear()}{" "}
      <Link
        color="inherit"
        href="https://frontdoorcorp.com/"
        underline="hover"
        target="_blank"
      >
        The FRONTdoor Collective
      </Link>
      {" | All Rights Reserved"}
      {""}
      {"."}
    </Typography>
  );
}

export function LoginFormModel(
  userName: string,
  password: string
): LoginRequest {
  return {
    userName: userName,
    password: password,
  };
}

const Login = () => {
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = Yup.object({
    userName: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
  });
  const formik = useFormik({
    initialValues: {
      userName: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const loginUserDetails = LoginFormModel(
          values.userName.trim(),
          values.password.trim()
        );
        const resultAction = await dispatch(
          userLogin(loginUserDetails)
        ).unwrap();
        if (resultAction.isSuccess) {
          localStorage.setItem("isLoggedIn", "true");
        }
        if (!resultAction.isSuccess) {
          toast.error(resultAction.message!, { toastId: "login-success" });
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, { toastId: "login-error" });
      }
    },
  });

  return (
    <>
      <div className="pageImageStyle">
        <Box
          sx={{
            margin: 0,
            padding: 0,
            textDecoration: "none",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "calc(10px + 2vmin)",
            position: "relative",
          }}
        >
          <Paper
            elevation={24}
            sx={{
              mt: 3,
              mb: 3,
            }}
          >
            <Container component="main" maxWidth="xs">
              <CssBaseline />
              <Box
                sx={{
                  marginTop: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="h4">Command Ops</Typography>
                <Avatar sx={{ m: 1, bgcolor: "#4eca0c" }}>
                  <LoginIcon />
                </Avatar>
                <Box sx={{ mt: 2 }}>
                  <form onSubmit={formik.handleSubmit}>
                    <TextField
                      fullWidth
                      id="userName"
                      name="userName"
                      label="User Name"
                      variant="standard"
                      size="medium"
                      value={formik.values.userName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.userName &&
                        Boolean(formik.errors.userName)
                      }
                      helperText={
                        formik.touched.userName && formik.errors.userName
                      }
                    />
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      label="Password/OTP"
                      variant="standard"
                      size="medium"
                      type={showPassword ? "text" : "password"}
                      sx={{ mt: 2 }}
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.password &&
                        Boolean(formik.errors.password)
                      }
                      helperText={
                        formik.touched.password && formik.errors.password
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      sx={{ mt: 2, mb: 2, textTransform: "none", fontSize: 14 }}
                      variant="contained"
                      size="medium"
                      fullWidth
                      type="submit"
                      disabled={!formik.isValid || formik.isSubmitting}
                    >
                      Sign In
                    </Button>
                    <Typography align="center">
                      <Link
                        href="/forgot-password"
                        sx={{ cursor: "pointer" }}
                        variant="body1"
                      >
                        Forgot Password?
                      </Link>
                    </Typography>
                  </form>
                </Box>
              </Box>
              <Copyright sx={{ mt: 5, mb: 4 }} />
            </Container>
          </Paper>
        </Box>
      </div>
      <Loader isLoading={formik.isSubmitting} />
    </>
  );
};

export default Login;
